import { FlexLayout, GridLayout, H4 } from '@deity/falcon-ui';
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { inputControlTypes, mapRecordToArray } from '@deity-io/di-data-basics';
import { type IDefinitionItem } from '@deity-io/di-data-definitions';
import type {
  IPropertyFieldItem,
  IPropertyItem,
  IPropertyList,
  IPropertyListItem
} from '@deity-io/di-data-structure-editor';
import FreeSoloList from 'components/FieldEditorControls/FreeSoloList';
import KeyValueTable from 'components/FieldEditorControls/KeyValueTable';
import NativeInputControl from 'components/FieldEditorControls/NativeInputControl';
import Options from 'components/FieldEditorControls/Options';
import EntityPicker from 'components/FlowEditor/components/panels/FlowComponentSettings/EntityPicker';
import _ from 'lodash';
import type { FC } from 'react';
import type React from 'react';
import { useState } from 'react';
import PropertyContainer from './PropertyContainer';
import { getInputControlType, hasAControl } from './propertyListHelper';

interface PropertyListEditorProps {
  locked?: boolean;
  propertyList: IPropertyList;
  /**
   * the "config" data
   */
  data: IPropertyBag;
  /**
   * the definition item we configure
   * used to get the native type if think it's for <T> types (variable type)
   */
  owner?: IDefinitionItem;
  onChange: (key: Array<string>, value: any) => void;
  onDelete?: (key: Array<string>) => void;
  pathPreFix?: Array<string>;
}
const PropertyListEditor: FC<PropertyListEditorProps> = ({
  locked,
  propertyList,
  data,
  owner,
  onChange,
  onDelete,
  pathPreFix = []
}) => {
  if (!data) {
    data = {};
  }
  const [customOverrides, setCustomOverrides] = useState({});
  const getAChildInputControlType = (property: IPropertyListItem): string => {
    if ('properties' in property) {
      const childTypes = mapRecordToArray(property.properties, child => getInputControlType(child, owner));
      if (childTypes.length) {
        return childTypes[0];
      }
    }

    return 'string';
  };

  const controlFactory = (property: IPropertyListItem, value: any, dryRun = false): React.ReactNode => {
    let inputControlType = getInputControlType(property, owner);

    if (inputControlType === inputControlTypes.dateTime) {
      inputControlType = inputControlTypes.string;
    }

    let propertyOptions;
    if ('options' in property) {
      propertyOptions = property.options;
    }

    switch (inputControlType) {
      case inputControlTypes.options:
        return (
          dryRun || (
            <Options
              property={property.name}
              value={value}
              options={propertyOptions}
              isEditable={locked !== true}
              onSelect={val => onChange([...pathPreFix, property.name], val)}
            />
          )
        );
      case inputControlTypes.boolean:
        return (
          dryRun || (
            <FlexLayout flexDirection="row" flexWrap="nowrap">
              No
              <NativeInputControl
                isHeaderHidden
                key={property.name}
                isEditable={locked !== true}
                description={property.description}
                notEmpty={property.notEmpty}
                required={property.required}
                value={value}
                property={property.name}
                controlType={inputControlType}
                onValueChange={(key, val) => {
                  onChange([...pathPreFix, key], val);
                }}
              />
              Yes
            </FlexLayout>
          )
        );
      case inputControlTypes.dateTime:
      case inputControlTypes.string:
      case inputControlTypes.number:
        return (
          dryRun || (
            <NativeInputControl
              isHeaderHidden
              key={property.name}
              isEditable={locked !== true}
              description={property.description}
              notEmpty={property.notEmpty}
              required={property.required}
              value={value}
              property={property.name}
              controlType={inputControlType}
              onValueChange={(key, val) => {
                onChange([...pathPreFix, key], val);
              }}
            />
          )
        );
      case inputControlTypes.dictionary:
        return (
          dryRun || (
            <KeyValueTable
              isEditable={locked !== true}
              notEmpty={property.notEmpty}
              required={property.required}
              key={property.name}
              fieldName={property.name}
              onTableValueChange={(dictKey, dictValue) =>
                onChange([...pathPreFix, property.name, ...dictKey], dictValue)
              }
              persistedValue={value}
            />
          )
        );

      case inputControlTypes.freeSoloList:
        property.notEmpty = true;

        return (
          dryRun || (
            <FreeSoloList
              key={property.name}
              name={property.name}
              inputControlType={getAChildInputControlType(property)}
              locked={locked}
              list={value}
              required={property.required}
              notEmpty={property.notEmpty}
              onChange={(soloIndex, soloValue) => onChange([...pathPreFix, property.name, ...soloIndex], soloValue)}
            />
          )
        );
      case undefined:
      case 'error':
        return <div>No input control configured for this value</div>;

      case inputControlTypes.structurePicker: // Flow
        return (
          <EntityPicker
            key={property.name}
            property={property.name}
            entityUUID={value}
            description={property.description || ''}
            entity="structure"
            label="Select a Structure"
            name={property.name}
            // error={propertyErrors.length > 0}
            isEditable={locked !== true}
            showEntityLink
            onChange={(key, val) => {
              onChange([...pathPreFix, key], val);
            }}
          />
        );

      case inputControlTypes.schemaPathPicker: // Flow
      case inputControlTypes.jsonEditor:
      case inputControlTypes.list:
      case inputControlTypes.mapping: // Flow
      case inputControlTypes.port: // Flow
      case inputControlTypes.restEndpoint: // Flow
      case inputControlTypes.schemaObjectPathPicker: // Flow
      case inputControlTypes.endpointPicker: // Flow
      case inputControlTypes.hidden:
      default:
        // Todo, a field without input control. but with children that have them (properties)
        return null;
    }
  };

  const showProperty = (property: IPropertyItem): boolean => {
    const type = getInputControlType(property, owner);
    if (type) {
      return hasAControl(type);
    }

    return false;
    // switch (getInputControlType(property)) {
    //   case inputControlTypes.hidden:
    //     return false;
    //   default:
    //     return true;
    // }
  };

  const inputControls = mapRecordToArray(propertyList, (property, key) => {
    try {
      const isPersisted = _.has(data, key) || _.has(customOverrides, key);
      const native = (property as IPropertyFieldItem).nativeType;

      // eslint-disable-next-line no-nested-ternary
      const myDef = native === 'string' ? '' : native === 'boolean' ? false : native === 'number' ? 0 : undefined;

      return showProperty(property) ? (
        <PropertyContainer
          key={key}
          showProperty={isPersisted || property.required}
          required={property.required}
          locked={property.required || locked}
          property={key}
          defaultValue={'defaultValue' in property ? property.defaultValue : undefined}
          description={property.description}
          defaultOverrideValue={myDef}
          onOverride={(overRideKey: string, setOverride: boolean, defVal: any) => {
            const newOverrideState = { ...customOverrides };
            if (setOverride) {
              newOverrideState[overRideKey] = true;
              setCustomOverrides(newOverrideState);
              onChange([...pathPreFix, overRideKey], defVal); // '' or 0 or false
            } else {
              delete newOverrideState[overRideKey];
              setCustomOverrides(newOverrideState);
              if (overRideKey in data) {
                if (onDelete) {
                  onDelete([...pathPreFix, overRideKey]);
                }
              }
            }
          }}
        >
          <FlexLayout
            p="xs"
            mt="xs"
            mb="xs"
            bg="panelLight"
            borderRadius="small"
            width="full"
            style={isPersisted || property.required ? {} : { display: 'none' }}
            gridColumn="1 / span 3"
          >
            {controlFactory(property, key in data ? data[key] : undefined)}
          </FlexLayout>
        </PropertyContainer>
      ) : null;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Error rendering property ', e);

      return <div>Error {e}</div>;
    }
  });

  const settingCount = inputControls.filter(item => item != null && item !== undefined).length;

  return settingCount ? (
    <FlexLayout bg="panelLight" mt="xs" mb="xs" borderRadius="small" p="xs">
      <GridLayout gridTemplateColumns="2fr 1fr 1fr" borderBottom="regular" p="xs" borderColor="secondary" width="full">
        <H4 fontWeight="bold" color="secondary">
          BEHAVIOR
        </H4>
        <H4 fontWeight="bold" color="secondary" justifySelf="center">
          DEFAULT
        </H4>
        <H4 fontWeight="bold" color="secondary" justifySelf="end">
          USE
        </H4>
      </GridLayout>
      {inputControls}
    </FlexLayout>
  ) : (
    <H4 css={{ marginTop: '20px' }}>No additional settings</H4>
  );
};
// style={isPersisted ? {} : { transition: 'all .2s ease-in-out', transform: `height 0px` }}
export default PropertyListEditor;
